import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface UsePaneUIStateOptions {
  paneCount: number;
  bodyRef: React.RefObject<HTMLDivElement | null>;
  onPaneCollapse?: (paneId: string, collapsed: boolean) => void;
  onPaneMaximize?: (paneId: string, maximized: boolean) => void;
}

export interface UsePaneUIStateResult {
  paneFractions: number[];
  collapsedPanes: Set<string>;
  maximizedPaneId: string | null;
  toggleCollapsed: (paneId: string) => void;
  toggleMaximized: (paneId: string) => void;
  clearCollapsedFor: (paneId: string) => void;
  setMaximizedIfMatch: (paneId: string) => void;
  startDividerDrag: (
    index: number,
    event: React.PointerEvent<HTMLDivElement>
  ) => void;
}

/**
 * Manages per-pane UI state — collapsed + maximized toggles, the flex
 * fractions between dividers, and the pointer-drag lifecycle for resizing.
 */
export const usePaneUIState = ({
  paneCount,
  bodyRef,
  onPaneCollapse,
  onPaneMaximize,
}: UsePaneUIStateOptions): UsePaneUIStateResult => {
  const [paneFractions, setPaneFractions] = useState<number[]>([]);
  const [collapsedPanes, setCollapsedPanes] = useState<Set<string>>(
    () => new Set()
  );
  const [maximizedPaneId, setMaximizedPaneId] = useState<string | null>(null);

  useEffect(() => {
    setPaneFractions(prev => {
      if (prev.length === paneCount) {
        return prev;
      }

      return Array.from({ length: paneCount }, () => 1 / paneCount);
    });
  }, [paneCount]);

  const toggleCollapsed = useCallback(
    (paneId: string) => {
      setCollapsedPanes(prev => {
        const next = new Set(prev);
        const willBeCollapsed = !next.has(paneId);

        if (willBeCollapsed) {
          next.add(paneId);
        } else {
          next.delete(paneId);
        }

        onPaneCollapse?.(paneId, willBeCollapsed);
        return next;
      });
    },
    [onPaneCollapse]
  );

  const toggleMaximized = useCallback(
    (paneId: string) => {
      setMaximizedPaneId(prev => {
        const next = prev === paneId ? null : paneId;

        onPaneMaximize?.(paneId, next === paneId);
        return next;
      });
    },
    [onPaneMaximize]
  );

  const clearCollapsedFor = useCallback((paneId: string) => {
    setCollapsedPanes(prev => {
      if (!prev.has(paneId)) {
        return prev;
      }

      const next = new Set(prev);

      next.delete(paneId);
      return next;
    });
  }, []);

  const setMaximizedIfMatch = useCallback((paneId: string) => {
    setMaximizedPaneId(prev => (prev === paneId ? null : prev));
  }, []);

  // Pointer drag lifecycle for the resize dividers. Listeners live on window
  // so releasing the pointer anywhere on the page ends the drag cleanly.
  const dividerDragRef = useRef<{
    index: number;
    startY: number;
    startFractions: number[];
    containerHeight: number;
  } | null>(null);

  const handleDividerPointerMove = useCallback((event: PointerEvent) => {
    const drag = dividerDragRef.current;

    if (!drag || drag.containerHeight <= 0) {
      return;
    }

    const deltaY = event.clientY - drag.startY;
    const deltaFraction = deltaY / drag.containerHeight;
    const { index, startFractions } = drag;
    const minFraction = 0.1;
    const aBase = startFractions[index];
    const bBase = startFractions[index + 1];
    const maxDelta = bBase - minFraction;
    const minDelta = -(aBase - minFraction);
    const clamped = Math.min(Math.max(deltaFraction, minDelta), maxDelta);
    const next = startFractions.slice();

    next[index] = aBase + clamped;
    next[index + 1] = bBase - clamped;
    setPaneFractions(next);
  }, []);

  const handleDividerPointerUp = useCallback(() => {
    dividerDragRef.current = null;
    window.removeEventListener('pointermove', handleDividerPointerMove);
    window.removeEventListener('pointerup', handleDividerPointerUp);
  }, [handleDividerPointerMove]);

  const startDividerDrag = useCallback(
    (index: number, event: React.PointerEvent<HTMLDivElement>) => {
      const container = bodyRef.current;

      if (!container) {
        return;
      }

      event.preventDefault();
      dividerDragRef.current = {
        index,
        startY: event.clientY,
        startFractions: paneFractions.slice(),
        containerHeight: container.getBoundingClientRect().height,
      };

      window.addEventListener('pointermove', handleDividerPointerMove);
      window.addEventListener('pointerup', handleDividerPointerUp);
    },
    [bodyRef, paneFractions, handleDividerPointerMove, handleDividerPointerUp]
  );

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handleDividerPointerMove);
      window.removeEventListener('pointerup', handleDividerPointerUp);
    },
    [handleDividerPointerMove, handleDividerPointerUp]
  );

  return {
    paneFractions,
    collapsedPanes,
    maximizedPaneId,
    toggleCollapsed,
    toggleMaximized,
    clearCollapsedFor,
    setMaximizedIfMatch,
    startDividerDrag,
  };
};
